import styles from "@/styles/contact-us.module.scss"
import { useEffect, useState, useRef } from "react";
import { Button, Form, Input, Select, message, Spin } from 'antd'
import contactApi from '@/service/modules/contactApi'
import systemApi from '@/service/modules/systemApi'
import myAccountApi from '@/service/modules/myAccountApi'
import UploadImage from "@/components/UploadImage";
import { uploadImageToServer } from "@/utils/upload"; // 导入公共方法
// @ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';

export default function ContactForm() {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [form] = Form.useForm()
  const { Option } = Select
  const { TextArea } = Input;

  const [subjects, setSubjects] = useState<any[]>([]); // 存储接口返回的 subjects 数据
  const [loading, setLoading] = useState<boolean>(true); // 数据加载状态
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null); // 当前选中的主问题
  const [selectedSub, setSelectedSub] = useState<string | null>(null); // 当前选中的子问题
  const [subjectNotes, setSubjectNotes] = useState<Record<string, string>>({}); // 文案数据
  const [subjectTypeMessageNotes, setSubjectTypeMessageNotes] = useState<Record<string, string>>({}); // message提示文案数据
  const [currentNotes, setCurrentNotes] = useState('')
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  interface DataSetting {
    [key: string]: string[]; // key 是字符串，值是字符串数组
  }
  const [dataSetting, setDataSetting] = useState<DataSetting>({});

  const [siteKey, setSiteKey] = useState('')
  const [recaptchaToken, setRecaptchaToken] = useState('');


  // 获取Recaptcha token
  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaToken(value || '')
  };
  const getRecaptchaSiteKey = async () => {
    try {
      let res = await systemApi.getRecaptchaSiteKey()
      if (res.data && res.data.siteKey) {
        setSiteKey(res.data.siteKey)
      }
    } catch (error) {
      console.error(error)
    }
  }

  // 模拟获取接口数据
  const fetchSubjects = async () => {
    setLoading(true);
    try {
      let response = await contactApi.getContactUsSetting()

      if (response && response.data.subjects) {
        setSubjects(response.data.subjects);
        setSubjectNotes(response.data.subjectNotes);
        setSubjectTypeMessageNotes(response.data.subjectTypeMessageNotes);
        setDataSetting(response.data.dataSetting);
      }
    } catch (error) {
      console.error("Failed to fetch subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  // 初始化选项的默认值
  useEffect(() => {
    if (subjects.length > 0) {
      const initialSubject = subjects[0].title;
      const initialSub = Object.keys(subjects[0].sub)[0] || null;
      setSelectedSubject(initialSubject);
      setSelectedSub(initialSub);
      form.setFieldsValue({
        mainSubject: initialSubject,
        subject: initialSub
      });
    }
  }, [subjects]);


  // 获取用户信息，填入表单
  const getCustomerInfo = async () => {
    try {
      let res = await myAccountApi.getCustomerInfo()
      if (res.data) {
        form.setFieldsValue({
          customerName: res.data?.customer.firstName || '',
          customerEmail: res.data?.customer.email || '',
          confirmEmail: res.data?.customer.email || ''
        });
      }
    } catch (error) {
      console.error(error)
    }
  }

  // 判断是否展示字段
  const shouldDisplayField = (field: string) => {
    return dataSetting[field]?.includes(selectedSub ?? ""); // 如果 selectedSub 是 null，用空字符串代替
  };

  useEffect(() => {
    getRecaptchaSiteKey()
    fetchSubjects();

    // 如果有登录，则获取用户信息
    if (localStorage.getItem('userEmail')) {
      getCustomerInfo()
    }
  }, []);

  // 主问题类型改变时
  const handleSubjectChange = (value: string) => {
    // 获取对应的子问题选项
    const currentSubject = subjects.find(subject => subject.title === value);
    const subOptions = currentSubject?.sub ? Object.keys(currentSubject.sub) : [];
    
    // 更新状态
    setSelectedSubject(value);
    
    // 自动选择第一个子选项
    const firstSubKey = subOptions[0] || null;
    setSelectedSub(firstSubKey);
    
    // 同步更新表单字段值
    form.setFieldsValue({
      subject: firstSubKey
    });
  };

  // 子问题类型改变时
  const handleSubChange = (value: string) => {
    setSelectedSub(value);
    form.setFieldsValue({ subject: value });
  };

  // 监听 selectedSub 更新 currentNotes
  useEffect(() => {
    if (selectedSub && subjectNotes[selectedSub]) {
      setCurrentNotes(subjectNotes[selectedSub]);
    } else {
      setCurrentNotes(""); // 如果没有匹配的文案，清空
    }
  }, [selectedSub, subjectNotes]);

  const handleOnUploaded = (values: any) => {
    setUploadedUrls(values)
  };

  // 校验成功，发起提交请求
  const onFinish = async(values: any) => {
    try {
      const recaptcha = recaptchaRef.current?.getValue();
      if (!siteKey || !recaptcha) {
        message.error('Please verify you are not a robot')
        return
      }
      
      // 定义带动态键的对象类型
      const result: Record<string, string> = {}; 
      
      const maxFields = 5;
      for (let i = 0; i < maxFields; i++) {
        result[`opPhoto${i + 1}`] = uploadedUrls[i] || ""; // 动态赋值
      }

      // 赋值subSubject
      const subSubjectList = subjects.find((subject) => subject.title === values.mainSubject)?.sub || {}
      let subSubject = subSubjectList[values.subject]

      const finalValues = { recaptcha, subSubject, ...values, ...result };

      setLoading(true)
      let res = await contactApi.submitContactUsData(finalValues)

      setLoading(false)
      if (res.data) {
        message.success('Submit Success')
        window.location.reload()
      }
    } catch (error: any) {
      setLoading(false)
      message.error(error.message)
    }
  }

  return (
    <Spin spinning={loading}>
      <div className={`${styles['contact-form-box']}`}>
        <Form
          name="basic"
          layout="vertical"
          size={'large'}
          style={{ width: '100%' }}
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          scrollToFirstError
        >
          <div className={styles['enquiry-type-select']}>
            <Form.Item 
              label="Main Reason"
              name="mainSubject"
              rules={[{ required: true, message: 'Main Reason is required' }]}
            >
              <Select
                style={{ width: '270px', marginRight: '24px' }}
                placeholder="Select one Main Reason"
                onChange={handleSubjectChange}
              >
                {subjects.map((subject) => (
                  <Option key={subject.title} value={subject.title}>
                    {subject.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* 子问题类型选择 */}
            <Form.Item 
              label="Subject"
              name="subject"
              rules={[{ required: true, message: 'Subject is required' }]}
            >
              <Select
                style={{ width: '270px', marginRight: '24px' }}
                placeholder="Select one Subject"
                onChange={handleSubChange}
                disabled={!selectedSubject}
              >
                {selectedSubject && 
                  Object.entries(
                    subjects.find(s => s.title === selectedSubject)?.sub || {}
                  ).map(([key, value]) => (
                    <Option key={key} value={key}>
                      {value as string}
                    </Option>
                  ))
                }
              </Select>
            </Form.Item>
          </div>

          {/* 文案展示 */}
          {currentNotes && (
            <div
              style={{
                marginBottom: "24px",
                padding: "12px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                backgroundColor: "#fafafa",
              }}
              dangerouslySetInnerHTML={{ __html: currentNotes }}
            ></div>
          )}


          {/* 动态表单内容 */}
          {selectedSub && 
            <div>

              {/* regEmail */}
              { shouldDisplayField("regEmail") &&
                <Form.Item
                  label="Registered Email Address"
                  name="regEmail"
                  rules={[
                    { required: true, message: `Registered Email Address is required`},
                    { type: 'email', message: `Invalid Email Address`},
                  ]}
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* opRegEmail */}
              { shouldDisplayField("opRegEmail") &&
                <Form.Item
                  label="Registered Email Address"
                  name="opRegEmail"
                  rules={[
                    { required: false, message: `Registered Email Address is required`},
                    { type: 'email', message: `Invalid Email Address`},
                  ]}
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* orderID */}
              { shouldDisplayField("orderID") &&
                <Form.Item
                  label={
                    <span>
                      Order Number(s) (Order ID): &nbsp;
                      <a href="/article/faq" target="_blank">
                        How to Find an Order ID
                      </a>
                    </span>
                  }
                  name="orderID"
                  rules={[
                    { required: true, message: `Order ID is required`}
                  ]}
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* opOrderID */}
              { shouldDisplayField("opOrderID") &&
                <Form.Item
                  label={
                    <span>
                      Order Number(s) (Order ID): &nbsp;
                      <a href="/article/faq" target="_blank">
                        How to Find an Order ID
                      </a>
                    </span>
                  }
                  name="opOrderID"
                  rules={[
                    { required: true, message: `Order ID is required`}
                  ]}
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* sku */}
              { shouldDisplayField("sku") &&
                <Form.Item
                  label={
                    <span>
                      Product Code (PID) of the item you ordered: &nbsp;
                      <a href="/article/faq" target="_blank">
                        How to Find a PID
                      </a>
                    </span>
                  }
                  name="sku"
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* opSku */}
              { shouldDisplayField("opSku") &&
                <Form.Item
                  label={
                    <span>
                      Product Code (PID): &nbsp;
                      <a href="/article/faq" target="_blank">
                        How to Find a PID
                      </a>
                    </span>
                  }
                  name="opSku"
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* Qty */}
              { shouldDisplayField("qty") &&
                <Form.Item
                  label="Qty"
                  name="qty"
                  rules={[
                    { required: true, message: `Qty is required`}
                  ]}
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* postCode */}
              { shouldDisplayField("postCode") &&
                <Form.Item
                  label={
                    <span>
                      Delivery Postcode: &nbsp;
                      <a href="http://www.nzpost.co.nz/tools/address-postcode-finder" target="_blank">
                        How to Find the Postcode
                      </a>
                    </span>
                  }
                  name="postCode"
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* opPostCode */}
              { shouldDisplayField("opPostCode") &&
                <Form.Item
                  label={
                    <span>
                      Delivery Postcode: &nbsp;
                      <a href="http://www.nzpost.co.nz/tools/address-postcode-finder" target="_blank">
                        How to Find the Postcode
                      </a>
                    </span>
                  }
                  name="opSku"
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* opPartNo */}
              { shouldDisplayField("opPartNo") &&
                <Form.Item
                  label={'Part Number: (Kindly refer to User manual if applicable or send us photo(s) of the part in question)'}
                  name="opPartNo"
                >
                  <Input style={{ width: '50%' }} />
                </Form.Item>
              }

              {/* opPhoto3 */}
              { (shouldDisplayField("opPhoto3") || shouldDisplayField("opPhoto5")) &&
                <Form.Item 
                  label={
                    <span style={{ paddingBottom: '24px' }}>
                      Please upload your photo. <span style={{ color: 'red' }}>(Photo size cannot be over 500kb)</span> (You can use this website to compress your photo)<br />
                      <strong>Note: </strong> Please make sure you take a photo that clearly shows the issue being reported. Our Customer Service team should be able to respond to your inquiry much quicker if they can easily identify the problem, which enables us to provide a faster resolution for your issue.
                    </span>
                  }
                >
                  <UploadImage
                    maxFiles={shouldDisplayField("opPhoto5") ? 5 : 3}
                    accept="image/*"
                    action={(file) =>
                      uploadImageToServer(file)
                    }
                    onUploaded={handleOnUploaded}
                  />
                </Form.Item>
              }

              {/* message */}
              <Form.Item
                label={
                  <span>
                    Type your Message: &nbsp;
                    (Note: Please provide detailed information regarding your issue. This is for our Customer Service team to immediately resolve your case.)
                  </span>
                }
                name="message"
                rules={[
                  { required: true, message: `Message is required`}
                ]}
                extra={subjectTypeMessageNotes[selectedSub] || null}
              >
                <TextArea maxLength={2000} showCount style={{ width: '50%' }} />
              </Form.Item>

              {/* customerName */}
              <Form.Item
                label="Your Name"
                name="customerName"
                rules={[
                  { required: true, message: `Your Name is required`}
                ]}
              >
                <Input style={{ width: '50%' }} />
              </Form.Item>

              {/* customerEmail */}
              <Form.Item
                label="Your Email Address"
                name="customerEmail"
                rules={[
                  { required: true, message: `Your Email Address is required`},
                  { type: 'email', message: `Invalid Email Address`},
                ]}
              >
                <Input style={{ width: '50%' }} />
              </Form.Item>

              {/* confirmEmail */}
              <Form.Item
                label="Confirm Your Email Address"
                name="confirmEmail"
                rules={[
                  { required: true, message: `Confirm Email Address is required`},
                  { type: 'email', message: `Invalid Email Address`},
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('customerEmail') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Your Email Address are not indentical.'));
                    },
                  }),
                ]}
              >
                <Input style={{ width: '50%' }} />
              </Form.Item>
            </div>
          }
          
          {
            !!(siteKey) &&
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              type={'audio'}
              onChange={handleRecaptchaChange}
            />
          }

          {/* 提交按钮 */}
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginTop: '16px', fontWeight: 700 }}>
              Send Now
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Spin>
  )
}